import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquarePlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "reviewed", label: "Reviewed" },
  { key: "archived", label: "Archived" },
];
const CAT_LABEL = {
  missing_feature: "Missing feature",
  improvement: "Could be improved",
  bug: "Something's not right",
  other: "Other",
};
const CAT_TONE = {
  missing_feature: "bg-blue-100 text-blue-700 border-blue-200",
  improvement: "bg-amber-100 text-amber-700 border-amber-200",
  bug: "bg-red-100 text-red-700 border-red-200",
  other: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function HaveYourSayPanel() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState({ submissions: [], counts: { total: 0, new: 0 } });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/feedback/submissions", { params: { status: filter } })
      .then((r) => setData(r.data || { submissions: [], counts: {} }))
      .catch(() => setData({ submissions: [], counts: {} }))
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/feedback/submissions/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Could not update"); }
  };

  return (
    <div className="space-y-4" data-testid="have-your-say-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><MessageSquarePlus className="w-5 h-5 text-[#FF5F15]" /> Have your say</h2>
          <p className="text-sm text-zinc-500">{data.counts?.total || 0} total · {data.counts?.new || 0} new — what customers want, what's missing, what could be better.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`hys-filter-${f.key}`}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 rounded-sm ${filter === f.key ? "border-[#FF5F15] bg-[#FF5F15] text-white" : "border-zinc-300 text-zinc-600 hover:border-zinc-400"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <div className="p-6 text-sm text-zinc-500">Loading…</div>
        : !data.submissions.length ? <div className="rounded-lg border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">No feedback yet.</div>
        : <div className="space-y-3">
            {data.submissions.map((s) => (
              <div key={s.id} className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900" data-testid={`hys-submission-${s.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={CAT_TONE[s.category] || CAT_TONE.other}>{CAT_LABEL[s.category] || s.category}</Badge>
                  {s.status === "new" && <Badge className="bg-[#FF5F15] text-white">New</Badge>}
                  {s.status === "reviewed" && <Badge variant="outline" className="text-emerald-600 border-emerald-200">Reviewed</Badge>}
                  {s.status === "archived" && <Badge variant="outline" className="text-zinc-400">Archived</Badge>}
                  <span className="ml-auto text-xs text-zinc-400">{s.created_at ? new Date(s.created_at).toLocaleString() : ""}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-800 dark:text-zinc-200">{s.message}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                  <span className="font-semibold text-zinc-700 dark:text-zinc-300">{s.org_name || "—"}</span>
                  <span>·</span>
                  <span>{s.user_name || "—"}{s.user_email ? ` (${s.user_email})` : ""}</span>
                  <span>·</span>
                  <span className="uppercase">{s.user_role || ""}</span>
                  <span className="ml-auto flex gap-2">
                    {s.status !== "reviewed" && <button className="font-semibold text-emerald-600 hover:underline" onClick={() => setStatus(s.id, "reviewed")} data-testid={`hys-review-${s.id}`}>Mark reviewed</button>}
                    {s.status !== "archived" && <button className="font-semibold text-zinc-500 hover:underline" onClick={() => setStatus(s.id, "archived")} data-testid={`hys-archive-${s.id}`}>Archive</button>}
                    {s.status !== "new" && <button className="font-semibold text-[#FF5F15] hover:underline" onClick={() => setStatus(s.id, "new")}>Reopen</button>}
                  </span>
                </div>
              </div>
            ))}
          </div>}
    </div>
  );
}
